/**
 * The way in to a launched headless session's messages, at the top of the Activity tab: the
 * full Messages section sits below the transcript, where nobody looks first. One line: whether
 * the session takes messages, "Message this session" (it focuses the box, draft kept) when it
 * does, and what to do when it does not (Approve and resume, or a new session).
 */
import type { Session } from '@orgus/protocol';
import { MessageSquare, PlugZap, RotateCcw, Unplug } from '@orgus/ui/icons';
import { Button, Spinner, Tooltip, cn } from '@orgus/ui';
import type { MessagesData } from './api.ts';
import { messageEntryView } from './model.ts';
import type { MessageEntryView } from './model.ts';
import { focusReply } from './replyDraft.ts';

/** The line itself: pure enough to render in tests. */
export function MessageEntryLine({ v, onMessage, onRetry }: { v: MessageEntryView; onMessage: () => void; onRetry: () => void }) {
  const Icon = v.kind === 'closed' ? Unplug : PlugZap;
  return (
    <div role="group" aria-label="Messages to this session" className="flex min-h-row items-center gap-3 rounded-card border border-border-subtle px-3 py-2">
      {v.kind === 'checking' ? <Spinner /> : <Icon className={cn('size-4 shrink-0', v.kind === 'open' ? 'text-passed' : 'text-fg-subtle')} aria-hidden />}
      <Tooltip content={v.reason}>
        <p className="min-w-0 flex-1 truncate text-sm" tabIndex={v.reason ? 0 : -1}>
          <span className={cn('font-medium', v.kind === 'open' ? 'text-fg' : 'text-fg-muted')}>{v.label}</span>
          {v.next && <span className="text-fg-muted">. {v.next}</span>}
        </p>
      </Tooltip>
      {v.kind === 'open' && (
        <Button size="sm" variant="secondary" icon={<MessageSquare />} onClick={onMessage}>
          Message this session
        </Button>
      )}
      {v.kind === 'unknown' && (
        <Button size="sm" variant="ghost" icon={<RotateCcw />} onClick={onRetry}>
          Check again
        </Button>
      )}
    </div>
  );
}

export function MessageEntry({ session, data, blocked }: { session: Session; data: MessagesData; blocked: boolean }) {
  const v = messageEntryView(data.input, { loading: data.loading, blocked });
  return <MessageEntryLine v={v} onMessage={() => focusReply(session.id)} onRetry={data.reload} />;
}
